import React, { useState } from 'react';
import cn from 'classnames';
import { Helmet } from 'react-helmet';
import { useSiteMetaData } from '../util/queries/site';
import { getImage } from '../util';

import WebLayout from '../layouts/Web';
import Wrapper from './Wrapper';
import EventBanner from './EventBanner';

import Intro from './issue/Intro';
import Quote from './issue/Quote';
import Book from './issue/Book';
import Podcast from './issue/Podcast';
import Artwork from './issue/Artwork';
import Mix from './issue/Mix';
import Conversation from './issue/Conversation';
import Playlist from './issue/Playlist';
import SubscribeForm from './SubscribeForm';

import * as styles from './Issue.module.css';
import { useSettings } from '../util/queries/settings';

const sections = [
  {
    key: 'intro',
    id: 'introduccion',
    label: 'Introducción',
    active: true
  },
  {
    key: 'quote',
    id: 'cita',
    label: 'Una cita'
  },
  {
    key: 'book',
    id: 'libro',
    label: 'Un libro'
  },
  {
    key: 'podcast',
    id: 'podcast',
    label: 'Un podcast'
  },
  {
    key: 'artwork',
    id: 'obra',
    label: 'Una obra'
  },
  {
    key: 'mix',
    id: 'mix',
    label: 'Un mix'
  },
  {
    key: 'conversation',
    id: 'conversacion',
    label: 'Una conversación remota'
  },
  {
    key: 'playlist',
    id: 'playlist',
    label: 'Un playlist'
  },
  {
    key: 'subscribe',
    id: 'suscribete',
    label: 'Suscríbete'
  }
];

const filterSections = (sections, issue) => {
  return sections.filter(s => issue[s.key]?.show);
};

const Issue = ({ issue }) => {
  const [showBanner, setShowBanner] = useState();

  const { subscribe, banner } = useSettings();

  const { siteUrl } = useSiteMetaData();
  if (!issue.intro) return null;

  return (
    <WebLayout
      title={issue.title}
      colors={issue.colors}
      theme={issue.theme}
      initialShowAbout={issue.initialShowAbout}
    >
      <Helmet>
        <meta property="og:title" key="og:title" content={issue.title} />
        <meta property="og:locale" key="og:locale" content="es_CL" />
        <meta charSet="utf-8" />
        <meta property="og:type" key="og:type" content="website" />
        <meta
          property="og:description"
          key="og:description"
          content={issue.intro.headline}
        />
        <meta
          property="og:image"
          key="og:image"
          content={`${siteUrl}${
            getImage(issue.intro.image).images.fallback.src
          }`}
        />
      </Helmet>
      <Wrapper tag="article" className={cn(styles.root)}>
        <EventBanner
          data={banner}
          isOpen={showBanner}
          setShow={setShowBanner}
        />

        <section id="introduccion">
          <Intro
            id="introduccion"
            number={issue.number}
            title={issue.title}
            intro={issue.intro}
            sections={filterSections(sections, { ...issue, subscribe })}
            slug={issue.slug}
            image={getImage(issue.intro.image)}
          />
        </section>
        <section id="cita">
          <Quote
            id="cita"
            text={issue.quote.text}
            author={issue.quote.author.name}
            subline={issue.quote.author.subline}
          />
        </section>
        <section id="libro">
          <Book
            id="libro"
            headline={issue.book.headline}
            intro={issue.book.intro}
            byLine={issue.book.byLine}
            text={issue.book.text}
            slug={issue.slug}
            book={{
              ...issue.book.book,
              image: getImage(issue.book.book.image)
            }}
          />
        </section>

        <div
          className={cn(styles.row, {
            [styles.oneChild]: !(issue.podcast.show && issue.artwork.show)
          })}
        >
          {/* HEADS UP!
           * To make this layout of a flippable card within a stretch flex layout
           * work there is some very specific CSS set up tailored to the exact
           * content (podcast and artwork) we expect inside this row module.
           *
           * Moving one of the modules out of the row you'll also want to check
           * the CSS, to make sure this has no unintended side effects.
           */}
          {issue.podcast.show && (
            <section id="podcast">
              <Podcast
                id="podcast"
                slug={issue.slug}
                headline={issue.podcast.title}
                byLine={issue.podcast.byLine}
                duration={issue.podcast.duration}
                intro={issue.podcast.intro}
                episodeId={issue.podcast.episodeId}
                spotifyUrl={issue.podcast.spotifyUrl}
                applePodcastsUrl={issue.podcast.applePodcastsUrl}
              />
            </section>
          )}
          {issue.artwork.show && (
            <section id="obra">
              <Artwork
                slug={issue.slug}
                id="obra"
                headline={issue.artwork.title}
                intro={issue.artwork.intro}
                info={issue.artwork.info}
                image={getImage(issue.artwork.image)}
                isInFlexLayout={issue.podcast.show}
              />
            </section>
          )}
        </div>

        {issue.mix.show && (
          <section id="mix">
            <Mix
              id="mix"
              slug={issue.slug}
              image={getImage(issue.mix.image)}
              imagePosition={issue.mix.imagePosition}
              headline={issue.mix.title}
              titleRight={issue.mix.titleRight}
              byLine={issue.mix.byLine}
              intro={issue.mix.intro}
              highlightBlock={issue.mix.highlightBlock}
              highlightBlockImage={getImage(issue.mix.highlightBlock.image)}
              blocks={issue.mix.blocks}
            />
          </section>
        )}

        {issue.conversation.show && (
          <section id="conversacion">
            <Conversation
              id="conversacion"
              slug={issue.slug}
              videoId={issue.conversation.videoId}
              image={getImage(issue.conversation.image)}
              meta={issue.conversation.meta}
              headline={issue.conversation.title}
              people={issue.conversation.people}
              text={issue.conversation.text}
              duration={issue.conversation.duration}
            />
          </section>
        )}

        {issue.playlist?.show && (
          <section id="playlist">
            <Playlist
              id="playlist"
              slug={issue.slug}
              headline={issue.playlist.title}
              byLine={issue.playlist.byLine}
              intro={issue.playlist.intro}
              spotifyUrl={issue.playlist.spotifyUrl}
            />
          </section>
        )}

        <section id="suscribete">
          <SubscribeForm id="suscribete" />
        </section>
      </Wrapper>
    </WebLayout>
  );
};

export default Issue;
